// lib
import { useContext, useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Transition } from 'react-transition-group'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/Sidebar'

import { DataContext } from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'

// components
import FiltersRowCompletes from '@aytm/app/stats/page/sidebar/filters/rows/FiltersRowCompletes'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

const FiltersGroupCompletes = ({ group }) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const { root, externalData } = useContext(DataContext)

  const [hidden, setHidden] = useState(
    Object.hasOwn(sidebarState, 'filters') && Object.hasOwn(sidebarState.filters || {}, group.key)
      ? sidebarState.filters[group.key].hidden
      : group.hidden || false
  )
  const [collapsed, setCollapsed] = useState(
    Object.hasOwn(sidebarState, 'filters') && Object.hasOwn(sidebarState.filters || {}, group.key)
      ? sidebarState.filters[group.key].collapsed
      : true
  )

  const containerRef = useRef()

  const getFilterValues = useCallback(
    (filter, index) => {
      const value = filter.disabled
        ? null
        : externalData.some(data => data.key == group.key) && externalData.find(data => data.key == group.key).values
          ? externalData.find(data => data.key == group.key).values[index]
          : null

      return null !== value ? JSON.stringify(value) : value
    },
    [group.key, externalData]
  )

  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'filters',
        state: {
          [group.key]: { collapsed, hidden }
        }
      }),
    [group.key, collapsed, hidden, sidebarDispatch]
  )

  useEffect(
    () =>
      setCollapsed(
        Object.hasOwn(sidebarState, 'filters') && Object.hasOwn(sidebarState.filters || {}, group.key)
          ? sidebarState.filters[group.key].collapsed
          : true
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group.key, sidebarState.filters]
  )

  return 0 < group.values.length || group.upload ? (
    hidden ? (
      <div
        data-testid={`stat-filters-group-${group.key}-switch`}
        className={style['filter-switch']}
        {...pressable(() => setHidden(false))}
      >
        Show {group.title.toLowerCase()}
      </div>
    ) : (
      <div data-testid={`stat-filters-group-${group.key}`} className={style['filter-group']}>
        <div id={`FiltersPanel-group-${group.key}`} className={style['filter-header']}>
          <div
            className={style['filter-subheader']}
            aria-expanded={!collapsed}
            {...pressable(() => setCollapsed(!collapsed))}
          >
            <span data-testid={`stat-filters-group-${group.key}-title`} className={style['filter-title']}>
              {group.title}
            </span>
            <span
              data-testid={`stat-filters-group-${group.key}-toggle`}
              className={classNames(style['filter-toggle'], { [style['filter-toggle-collapsed']]: collapsed })}
            >
              <i className="icon-aytmfd-chevron-bracket" />
            </span>
          </div>
          {group.help && root ? (
            <SidebarPopup root={root} help={group.help} width={300 < group.help.length ? '600px' : '300px'} />
          ) : null}
        </div>
        <Transition in={!collapsed} timeout={500} nodeRef={containerRef}>
          {status => (
            <div
              ref={containerRef}
              data-testid={`stat-filters-group-${group.key}-container`}
              className={`${style['filter-container']} ${style['transition-panel']}${
                status ? ' ' + style['transition-panel-' + status] : ''
              }`}
            >
              {'exited' === status
                ? null
                : group.values.map((filter, index) => (
                    <FiltersRowCompletes
                      key={filter.subkey ? `${filter.key}${filter.subkey}` : filter.key}
                      filter={filter}
                      filterValues={getFilterValues(filter, index)}
                      filterOptions={filter.options}
                    />
                  ))}
            </div>
          )}
        </Transition>
      </div>
    )
  ) : null
}

FiltersGroupCompletes.propTypes = {
  group: PropTypes.object
}

FiltersGroupCompletes.displayName = 'FiltersGroupCompletes'

export default FiltersGroupCompletes
